import {
    envFlags,
    FLAGS,
    getQueryParamsAndQueryString,
} from '@thoughtspot/env-flags';
import { create } from '@thoughtspot/logger';
import {
    getFormatLocales,
    getNarrowCurrencyCodes,
    getPreferredCurrencyFormat,
    isKPIChartEnhancementsEnabled,
    isNarrowCurrencySymbolEnabled,
    isOrgPerUrlEnabled,
} from '@thoughtspot/session-service';
import _ from 'lodash';
import { browserInfo } from '../../utils/browser.util';
import {
    DEFAULT_DOWNLOAD_FILENAME,
    downloadFileUtil,
    filenamify,
    getFileName,
    KPI_CHART_ENHANCEMENTS_PARAM,
    LIVEBOARD_STYLING_ENABLED_PARAM,
} from '../../utils/file-download.util';
import { getTextFromHTMLTags } from './answer.util';

const logger = create('answer-download-service');

export const sanitizeExportFileName = (exportFileName: string) => {
    const fileName = filenamify(
        getTextFromHTMLTags(exportFileName, DEFAULT_DOWNLOAD_FILENAME),
        { replacement: '' },
    );
    return browserInfo.isSafari ? fileName.replace(/%/g, '') : fileName;
};

const appendLocaleOverrideParams = (fetchUrl: string) => {
    let appendUrl = '';
    const { dateFormatLocale, numberFormatLocale } = getFormatLocales();
    const currencyFormat = getPreferredCurrencyFormat();
    const { queryParamsMultiValue } = getQueryParamsAndQueryString({
        includeSearch: true,
        includeHash: true,
    });
    const annotationText =
        queryParamsMultiValue[FLAGS.downloadAnnotationText.name];
    if (dateFormatLocale) {
        appendUrl += `&dateFormatLocale=${dateFormatLocale}`;
    }
    if (numberFormatLocale) {
        appendUrl += `&numberFormatLocale=${numberFormatLocale}`;
    }
    if (currencyFormat) {
        appendUrl += `&currencyFormat=${currencyFormat}`;
    }
    if (!_.isEmpty(annotationText)) {
        if (annotationText.length !== 1) {
            logger.warn(
                'Only one downloadAnnotationText supported, skipping rest.',
            );
        }
        appendUrl += `&${FLAGS.downloadAnnotationText.name}=${annotationText[0]}`;
    }
    return fetchUrl + appendUrl;
};

export const onDownloadAnswerUtil = async (fetchUrl: string) => {
    const finalUrl = appendLocaleOverrideParams(fetchUrl);
    return fetch(finalUrl)
        .then(async res => {
            if (!res?.ok) {
                const errResponse = await res.json();
                logger.error(errResponse);
                throw new Error(
                    `Answer download failed due to ${res?.statusText}`,
                );
            }
            const disposition = res?.headers?.get('Content-Disposition');
            const fileName = getFileName(disposition);
            return {
                blob: await res?.blob(),
                fileName,
            };
        })
        .then(file => {
            return downloadFileUtil(file);
        });
};

export const downloadAnswerCSV = async (
    sessionId: string,
    genNo: number,
    userLocale: string,
    exportFileName: string,
): Promise<{ fileName?: string }> => {
    const fetchUrl = `/prism/download/answer/csv?sessionId=${sessionId}&genNo=${genNo}&userLocale=${userLocale}&exportFileName=${encodeURIComponent(
        sanitizeExportFileName(exportFileName),
    )}`;
    return onDownloadAnswerUtil(fetchUrl);
};

const getNarrowCurrencyParams = () => {
    let narrowCurrencyParams = `&isNarrowCurrencySymbolEnabled=${isNarrowCurrencySymbolEnabled()}`;

    const narrowCurrencyCodes = getNarrowCurrencyCodes();
    if (isNarrowCurrencySymbolEnabled() && narrowCurrencyCodes !== undefined)
        narrowCurrencyParams += `&narrowCurrencyCodes=${narrowCurrencyCodes.toString()}`;

    return narrowCurrencyParams;
};

export const downloadAnswerPDF = async (
    sessionId: string,
    genNo: number,
    userLocale: string,
    exportFileName: string,
): Promise<{ fileName?: string }> => {
    let fetchUrl = `/prism/download/answer/pdf?sessionId=${sessionId}&genNo=${genNo}&userLocale=${userLocale}&exportFileName=${encodeURIComponent(
        sanitizeExportFileName(exportFileName),
    )}`;

    if (isNarrowCurrencySymbolEnabled() !== undefined)
        fetchUrl += getNarrowCurrencyParams();

    return onDownloadAnswerUtil(fetchUrl);
};

export const downloadAnswerPNG = async (
    sessionId: string,
    deviceScaleFactor: number,
    genNo: number,
    omitBackground: boolean,
    userLocale: string,
    exportFileName: string,
    authType: string | null,
    isLiveboardStylingEnabled?: boolean,
): Promise<{ fileName?: string }> => {
    let fetchUrl = `/prism/download/answer/png?sessionId=${sessionId}&deviceScaleFactor=${deviceScaleFactor}&genNo=${genNo}&omitBackground=${omitBackground}&userLocale=${userLocale}&exportFileName=${encodeURIComponent(
        sanitizeExportFileName(exportFileName),
    )}`;

    if (authType) {
        fetchUrl += `&authType=${authType}`;
    }

    if (isNarrowCurrencySymbolEnabled() !== undefined)
        fetchUrl += getNarrowCurrencyParams();

    if (!_.isNil(isLiveboardStylingEnabled)) {
        fetchUrl += `&${LIVEBOARD_STYLING_ENABLED_PARAM}=${isLiveboardStylingEnabled}`;
    }

    if (isKPIChartEnhancementsEnabled()) {
        fetchUrl += `&${KPI_CHART_ENHANCEMENTS_PARAM}=true`;
    }

    return onDownloadAnswerUtil(fetchUrl);
};

export const downloadAnswerXLSX = async (
    sessionId: string,
    genNo: number,
    userLocale: string,
    exportFileName: string,
): Promise<{ fileName?: string }> => {
    const fetchUrl = `/prism/download/answer/xlsx?sessionId=${sessionId}&genNo=${genNo}&userLocale=${userLocale}&exportFileName=${encodeURIComponent(
        sanitizeExportFileName(exportFileName),
    )}`;
    return onDownloadAnswerUtil(fetchUrl);
};
